<template>
  <div class="d-flex justify-center autocomplete-container">
    <v-autocomplete
      v-model="selectedItems"
      id="autocomplete"
      append-icon="add"
      multiple
      dense
      rounded
      hide-details
      :items="autocompletionItems"
      :search-input.sync="searchInput"
      @change="change"
    >
      <template v-slot:selection="selection">
        <div class="tag-chip-container">
          <v-chip
            close
            close-icon="close"
            @click:close="removedTag(selection.index)"
            class="tag-chip"
            :color="getChipColor(selection.item)"
          >
            {{ selection.item.text }}
          </v-chip>
        </div>
      </template>
      <template v-slot:item="item">
        <div>
          <div v-if="item.item.type === 'existing'">
            <span class="autocomplete-text-item">
              {{ item.item.text }}
            </span>
          </div>
          <div v-if="item.item.type === 'new'">
            <span class="autocomplete-create-text">
              <i>Créer</i>
            </span>
            <span class="autocomplete-text-item">
              '{{ item.item.text }}'
            </span>
          </div>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>
<script>

const sanitizeTag = (tag) => {
  if (tag.text) {
    return tag.text.toUpperCase();
  }

  return tag.toUpperCase();
};

export default {
  name: 'TagsAutocomplete',
  props: {
    tags: {
      type: Array,
      required: true,
    },
    defaultSelectedTags: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      hasAddedAutocompletionItem: false,
      autocompletionItems: [],
      selectedItems: [],
      registeredItems: new Set(),
      originalItems: new Map(),
      searchInput: '',
    };
  },
  watch: {
    defaultSelectedTags() {
      this.addMissingDefaultTagsToCompletion();
      this.updateSelectedItems();
    },
    searchInput() {
      if (!this.searchInput || this.searchInput === '') {
        if (this.hasAddedAutocompletionItem) {
          this.autocompletionItems.pop();
          this.hasAddedAutocompletionItem = false;
        }
        return;
      }

      const currentSearchExactMatchExist = this.autocompletionItems.find(item => sanitizeTag(item)
        === sanitizeTag(this.searchInput));
      if (currentSearchExactMatchExist !== undefined) return;


      if (!this.hasAddedAutocompletionItem) {
        this.hasAddedAutocompletionItem = true;
        this.autocompletionItems.push({
          text: this.searchInput,
          source: 'user',
          type: 'new',
        });
      } else {
        this.autocompletionItems.pop();
        this.autocompletionItems.push({
          text: this.searchInput,
          type: 'new',
        });
      }
    },
    tags() {
      this.updateCompletionItems();
    },
  },
  methods: {
    updateCompletionItems() {
      this.autocompletionItems = [];
      this.tags.forEach(tag => this.autocompletionItems.push({
        type: 'existing',
        source: tag.source,
        text: tag.text,
      }));
      this.tags.forEach(tag => this.originalItems.set(sanitizeTag(tag.text), tag));
      this.addMissingDefaultTagsToCompletion();
    },
    addMissingDefaultTagsToCompletion() {
      this.defaultSelectedTags.forEach(tag => {
        if (!this.originalItems.has(sanitizeTag(tag.text))) {
          this.originalItems.set(sanitizeTag(tag.text), tag);
          this.autocompletionItems.push({
            type: 'existing',
            source: tag.source,
            text: tag.text,
          });
        }
      });
    },
    updateSelectedItems() {
      this.selectedItems = [];
      this.defaultSelectedTags.forEach(tag => {
        this.selectedItems.push(tag.text);
      });
    },
    removedTag(index) {
      this.selectedItems.splice(index, 1);
      this.change(this.selectedItems);
    },
    change(allAddedItems) {
      const hasAddedItem = allAddedItems.length > this.registeredItems.size;
      const hasRemovedItem = allAddedItems.length < this.registeredItems.size;
      const updatedRegisteredItems = new Set(allAddedItems.map(item => sanitizeTag(item)));

      if (hasAddedItem) {
        const addedItem = sanitizeTag(allAddedItems[allAddedItems.length - 1]);
        this.onAddedItem(addedItem);
      }

      if (hasRemovedItem) {
        const removedItem = Array.from(this.registeredItems)
          .find(item => !updatedRegisteredItems.has(item));
        this.onRemovedItem(removedItem);
      }

      this.searchInput = '';
      this.registeredItems = updatedRegisteredItems;
      this.emitSelectedTags(allAddedItems);
    },
    emitSelectedTags(allAddedItems) {
      const existingTags = [];
      const newTags = [];

      allAddedItems.forEach((item) => {
        const sanitizeItem = sanitizeTag(item);
        if (this.originalItems.has(sanitizeItem)) {
          existingTags.push(this.originalItems.get(sanitizeItem));
        } else {
          newTags.push(item);
        }
      });
      const event = {
        existingTags,
        newTags,
      };
      this.$emit('selectedTags', event);
    },
    onAddedItem(item) {
      if (this.originalItems.has(item) && this.hasAddedAutocompletionItem) {
        this.autocompletionItems.pop();
      }
      this.hasAddedAutocompletionItem = false;
    },
    onRemovedItem(item) {
      if (!this.originalItems.has(item)) {
        let indexToRemove = -1;
        this.autocompletionItems.find((autocompletionItem, index) => {
          indexToRemove = index;
          return sanitizeTag(autocompletionItem) === sanitizeTag(item);
        });
        this.autocompletionItems.splice(indexToRemove, 1);
      }
    },
    getChipColor(item) {
      if (item.source === 'organization') {
        return '#FFEFAF';
      }

      return '#AFE1FF';
    },
  },
  created() {
    this.updateCompletionItems();
  },
};
</script>
<style scoped>
.autocomplete-container {
  width: 100%;
  margin-bottom: 6px;
}
.autocomplete-text-item {
  font-size: 16px;
  padding: 2px;
  font-weight: 500;
}
.autocomplete-create-text {
  font-size: 14px;
  padding-left: 8px;
  padding-right: 8px;
  font-weight: 400;
}
.tag-chip {
  font-size: 12px;
  font-weight: 525;
  height: 24px;
  border-radius: 5px;
}
.tag-chip-container {
  padding-bottom: 2px;
  padding-top: 2px;
}
</style>
